import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  HStack,
  Image,
  LightMode,
  Text,
  VStack
} from "@chakra-ui/react"
import {
  FieldTip,
  TippedField
} from "@pathwright/ux/src/components/form/TippedField"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/web/src/modules/pathicon/Pathicon"
import { Form, Formik } from "formik"
import { useEffect, useState } from "react"
import { toFormikValidate } from "zod-formik-adapter"
import { RegistrationRoleEnum } from "../../api/generated"
import {
  UseCohortsUpgradeInlineGateReturn,
  useCohortsUpgradeInlineGate
} from "../../cohort/create/UpgradeCohortsGate"
import BGMode from "../../components/BGMode"
import KeyboardFormSubmit from "../../components/formik/KeyboardFormSubmit"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import usePathCreatorFlowSubmit from "././usePathCreatorFlowSubmit"
import CohortSelector from "./CohortSelector"
import ResourceSelector from "./ResourceSelector"
import RoleSelector, { RoleSelectorOnChange } from "./RoleSelector"
import FormSubmitButton from "./components/FormSubmitButton"
import {
  PathCreatorRawValues,
  PathCreatorResult,
  PathCreatorValidValues,
  SelectedCohort,
  SelectedResource,
  isExistingItem,
  isNewItem,
  pathCreatorValidValuesSchema
} from "./state"
import { solid, transient } from "./style"

const validator = toFormikValidate(pathCreatorValidValuesSchema)

// Override user's role if we should force the editor role.
function useShouldForceDesignRole() {
  const { me } = usePathwrightContext()
  const shouldForceDesignRole = !!me.permissions?.can_edit_library
  return shouldForceDesignRole && false // For now, not forcing Design role.
}

function CohortsUpgradeGate({
  values,
  onChange
}: {
  values: PathCreatorRawValues
  onChange: (gate: UseCohortsUpgradeInlineGateReturn | null) => void
}) {
  // When user is attempting to create new Cohort for existing resource,
  // get the resource ID of the selected resource to determine permissions
  // to create Cohort.
  const selectedResourceId =
    isExistingItem(values.resource) && isNewItem(values.cohort)
      ? values.resource.id
      : null

  const cohortsUpgradeGate = useCohortsUpgradeInlineGate(selectedResourceId!)

  useEffect(() => {
    onChange(cohortsUpgradeGate)
  }, [!!cohortsUpgradeGate])

  if (!cohortsUpgradeGate) return null

  return (
    <Alert
      status="info"
      variant="solid"
      borderRadius="20px"
      background="whiteAlpha.500"
      color="black"
      bg="whiteAlpha.600"
      backdropFilter="blur(20px)"
    >
      <HStack>
        <Pathicon
          icon={cohortsUpgradeGate.icon}
          style={{
            color: "black"
          }}
        />
        <Box>
          <AlertTitle>{cohortsUpgradeGate.heading}</AlertTitle>
          <AlertDescription>{cohortsUpgradeGate.body}</AlertDescription>
        </Box>
      </HStack>
    </Alert>
  )
}

export type OnComplete = (state: PathCreatorResult) => Promise<void> | void

type PathCreatorFormProps = {
  targetUserId: number
  initialValue?: PathCreatorRawValues
  onComplete?: OnComplete
  // Whether the PathCreatorForm inputs should auto show their related tooltip when
  // user focuses the input.
  autoShowTips?: boolean
}

function PathCreatorForm({
  targetUserId,
  onComplete,
  initialValue = { resource: null, cohort: null },
  autoShowTips = false
}: PathCreatorFormProps) {
  const [error, setError] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [cohortsUpgradeGate, setCohortsUpgradeGate] =
    useState<UseCohortsUpgradeInlineGateReturn | null>(null)

  const onFlowSubmit = usePathCreatorFlowSubmit()

  async function onSubmit(values: PathCreatorRawValues) {
    try {
      setIsSubmitting(true)
      const state = await onFlowSubmit({
        targetUserId,
        ...(values as PathCreatorValidValues)
      })

      // Call the onComplete callback.
      await onComplete?.(state)
    } catch (error) {
      setError(error as unknown as string)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Validate form based on expected action
  async function validate(values: PathCreatorRawValues) {
    // Check if the next state should be shown the cohorts upgrade gate.
    // Consider form invalid if so.
    if (cohortsUpgradeGate) {
      // NOTE: This error isn't displayed (neither is it intended to).
      return "Cannot create Cohort, please upgrade your plan."
    }

    return validator(values as PathCreatorValidValues)
  }

  const shouldForceDesignRole = useShouldForceDesignRole()

  const tipProps: FieldTip = {
    mode: autoShowTips ? "auto" : "manual",
    icon: "lightbulb",
    activeIcon: "lightbulb-on-fill",
    popover: {
      placement: "top-end",
      trigger: "click"
    }
  }

  return (
    <Formik
      initialValues={initialValue}
      // Necessary for keeping form up to date with pathCreatorState.
      // enableReinitialize
      validateOnMount
      // Prevent the default validation behavior on blur which can lead to validating
      // with stale data due to auto-focusing inputs on mount which must trigger a
      // validation call with stale data.
      validateOnBlur={false}
      validate={validate}
      onSubmit={onSubmit}
    >
      {(form) => (
        <BGMode
          exclusions={[`[data-theme="dark"][id$="-listbox"]`]}
          input={{
            color: "whiteAlpha.800",
            // Attempt to override browser autofill styles, but not working.
            bg: "light-dark(transparent, transparent) !important",
            borderColor: "whiteAlpha.600",
            _placeholder: { color: "whiteAlpha.600" }
          }}
        >
          <Box as={Form} w="100%">
            <KeyboardFormSubmit />
            {!!form.values.resource &&
              !!(
                ("background_image" in form.values.resource
                  ? form.values.resource.background_image
                  : form.values.resource.image) || ""
              ) && (
                <Image
                  src={
                    ("background_image" in form.values.resource
                      ? form.values.resource.background_image
                      : form.values.resource.image) || ""
                  }
                  w="100%"
                  h="100%"
                  position="absolute"
                  inset={0}
                  borderRadius="xl"
                  zIndex={-1}
                  fallback={
                    <Box
                      w="100%"
                      h="100%"
                      position="absolute"
                      inset={0}
                      borderRadius="xl"
                      {...transient}
                    />
                  }
                />
              )}
            <VStack spacing={2} {...solid}>
              <Box position="relative" zIndex={3} w="100%">
                <TippedField
                  tip={{
                    ...tipProps,
                    placeholder: `E.g., "Web Design 101", "New Team Member", "Leadership Basics",...`,
                    title: (
                      <>
                        <Text as="span" color="inherit">
                          A <strong>Path</strong> is a series of steps to
                          completing a class, online course, team onboarding,
                          learning event, or any learning experience.{" "}
                          <Link
                            href="https://help.pathwright.com/en/articles/258211-start-designing-your-course-path"
                            target="_blank"
                            textDecor="underline"
                            cursor="pointer"
                            rightIcon={<Pathicon icon="chevron-right" />}
                            iconSpacing={0}
                          >
                            More about Paths
                          </Link>
                        </Text>
                      </>
                    )
                  }}
                  as={ResourceSelector}
                  name="resource"
                  mode={
                    initialValue?.resource &&
                    "id" in initialValue?.resource &&
                    initialValue?.resource?.id === -1
                      ? "create"
                      : "select"
                  }
                  label="Path name"
                  hideLabel
                  onChange={async (item: SelectedResource | null) => {
                    const values = {
                      resource: item,
                      cohort: form.values.cohort
                    }

                    // Clear out any "existing" cohort when changing resource.
                    if (
                      form.values.resource &&
                      "id" in form.values.resource &&
                      form.values.resource?.id !== item?.id &&
                      isExistingItem(form.values.cohort)
                    ) {
                      values.cohort = null
                    }

                    await form.setValues(values, true)
                  }}
                  isDisabled={isSubmitting}
                />
              </Box>
              <Box position="relative" zIndex={2} w="100%">
                <TippedField
                  tip={{
                    ...tipProps,
                    placeholder: `E.g., "Spring 2025", "Anytime access", "New team members"...`,
                    title: (
                      <>
                        <Text as="span" color="inherit">
                          A <strong>Cohort</strong> is a group of people taking
                          a Path together. Cohorts can have unique discussions,
                          teachers, schedules, prices, and more. A Path can have
                          one or hundreds of Cohorts.{" "}
                          <Link
                            href="https://help.pathwright.com/en/articles/459249-cohorts-help-everyone-learn-together-again"
                            target="_blank"
                            textDecor="underline"
                            cursor="pointer"
                            rightIcon={<Pathicon icon="chevron-right" />}
                            iconSpacing={0}
                          >
                            Learn more
                          </Link>
                        </Text>
                      </>
                    )
                  }}
                  as={CohortSelector}
                  name="cohort"
                  mode={
                    form?.values.resource &&
                    "id" in form?.values.resource &&
                    form?.values.resource?.id === -1
                      ? "create"
                      : "create-or-select"
                  }
                  label="Cohort name"
                  hideLabel
                  targetUserId={targetUserId}
                  selectedResourceId={
                    form.values.resource && "id" in form.values.resource
                      ? form.values.resource.id
                      : null
                  }
                  onChange={async (item: SelectedCohort) => {
                    // Preserve selected role.
                    await form.setValues(
                      {
                        resource: form.values.resource,
                        cohort: item
                          ? {
                              ...item,
                              role: form.values.cohort?.role || null
                            }
                          : null
                      },
                      true
                    )
                  }}
                  isDisabled={isSubmitting || !form.values.resource?.name}
                />
              </Box>

              <Box position="relative" zIndex={1} w="100%">
                <TippedField
                  name="roles"
                  label="Your Roles"
                  hideLabel
                  as={RoleSelector}
                  tip={{
                    ...tipProps,
                    title: (
                      <>
                        <Text as="span" color="inherit">
                          Choose which roles you'd like to play in this Path{" "}
                          {">"} Cohort (you can change this anytime).{" "}
                          <Link
                            href="https://help.pathwright.com/en/articles/2083448-pathwright-member-roles"
                            target="_blank"
                            textDecor="underline"
                            cursor="pointer"
                            rightIcon={<Pathicon icon="chevron-right" />}
                            iconSpacing={0}
                          >
                            Learn more about roles
                          </Link>
                        </Text>
                      </>
                    )
                  }}
                  value={{
                    resourceRole: shouldForceDesignRole
                      ? RegistrationRoleEnum.Editor
                      : form.values.resource?.role,
                    cohortRole: form.values.cohort?.role
                  }}
                  onChange={async (
                    roles: Parameters<RoleSelectorOnChange>[0]
                  ) => {
                    const values = {
                      resource: {
                        ...form.values.resource!,
                        role: roles.resourceRole
                      },
                      cohort: {
                        ...form.values.cohort!,
                        role: roles.cohortRole
                      }
                    }
                    // Setting values without validating.
                    await form.setValues(values, true)
                  }}
                  atmosphericImage={form.values.resource?.image}
                  isDisabled={isSubmitting || !form.values.cohort?.name}
                />
              </Box>

              <CohortsUpgradeGate
                values={form.values}
                onChange={setCohortsUpgradeGate}
              />

              {!!error && (
                <Alert
                  status="error"
                  borderRadius="20px"
                  backdropFilter="blur(20px)"
                  colorScheme="red"
                >
                  <HStack>
                    <Pathicon
                      icon="caution-triangle"
                      style={{
                        color: "var(--alert-fg)"
                      }}
                    />
                    <Box>
                      <AlertTitle>Error</AlertTitle>
                      <AlertDescription>{error.toString()}</AlertDescription>
                    </Box>
                  </HStack>
                </Alert>
              )}

              <LightMode>
                <HStack w="100%" justifyContent="center">
                  <FormSubmitButton
                    isLoading={isSubmitting}
                    isDisabled={!!cohortsUpgradeGate}
                  />
                </HStack>
              </LightMode>
            </VStack>
          </Box>
        </BGMode>
      )}
    </Formik>
  )
}

export default PathCreatorForm
